import { useEffect, useRef } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@lato/client/contexts/AuthContext";
import { ErrorBoundary } from "@lato/client/components/ErrorBoundary";
import { Button } from "@lato/client/components/ui/button";
import { logger } from "@lato/client/lib/logger";
import { clearAuthBlock } from "@lato/client/lib/authBlock";
import { AuthForm } from "@/components/AuthForm";
import { SetupSignOut } from "@/components/SetupSignOut";
import { signInWithGoogle, signInWithMicrosoft, signInWithWorkOS } from "@/lib/web-auth";
import { AcceptInviteError, useAcceptInvite, useInvitePreview } from "@/hooks/useCloudChat";
import { inviteView } from "./inviteState";

/** Centered single-message layout matching the invite-wall screen. */
function Shell({ children, signOut = false }: { children: React.ReactNode; signOut?: boolean }) {
  return (
    <div className="relative flex h-full w-full flex-col p-6">
      {signOut && <SetupSignOut />}
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="w-full max-w-sm space-y-4 text-center">{children}</div>
      </div>
      <div className="flex justify-center pb-4">
        <img src="/assets/lato-grid-black.svg" alt="Lato" className="h-8 object-contain dark:hidden" />
        <img
          src="/assets/lato-grid-white.svg"
          alt="Lato"
          className="h-8 object-contain hidden dark:block"
        />
      </div>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <Shell>
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-body text-muted-foreground">{label}</p>
    </Shell>
  );
}

/** Escape hatch every terminal wall carries — no invite state may dead-end. */
function GoHome({ signedIn }: { signedIn: boolean }) {
  return (
    <Button asChild variant="outline" className="h-10">
      <Link to="/" replace>
        {signedIn ? "Go to my workspace" : "Go to sign in"}
      </Link>
    </Button>
  );
}

function InvitePage() {
  const { token } = Route.useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const preview = useInvitePreview(token);
  const accept = useAcceptInvite();
  const fired = useRef(false);

  const orgName = preview.data?.org_name ?? "your workspace";
  const inviter = preview.data?.inviter ?? "your admin";

  // Once a session exists, redeem unconditionally — the backend accept is the
  // source of truth and is idempotent for existing members (a consumed or even
  // expired invite still resolves to their membership). The frontend must not
  // pre-guess from preview flags; that pre-guessing is what used to strand
  // signed-in members on a dead-end "already used" wall. The ref guards
  // against re-fire on re-render.
  useEffect(() => {
    if (!session || fired.current) return;
    fired.current = true;
    accept.mutate(token, { onSuccess: () => {
      clearAuthBlock();
      void navigate({ to: "/", replace: true });
    } });
  }, [session, token, accept, navigate]);

  // If the accept 401'd and signed us out, clear the one-shot guard + stale
  // error so re-authenticating *in place* (email-OTP sets the session without a
  // remount) re-triggers the auto-accept above instead of stranding the now
  // signed-in user on the AuthForm.
  useEffect(() => {
    if (!session) {
      fired.current = false;
      if (accept.isError) accept.reset();
    }
  }, [session, accept]);

  const view = inviteView({
    session: !!session,
    preview: {
      loading: preview.isLoading,
      notFound: !preview.isLoading && !preview.isError && preview.data == null,
      failed: preview.isError,
      expired: preview.data?.expired ?? false,
      accepted: preview.data?.accepted ?? false,
    },
    accept: {
      status: accept.status,
      errorStatus: accept.error instanceof AcceptInviteError ? accept.error.status : undefined,
      errorMessage: accept.error?.message,
    },
  });

  // Observability for the dead-end class of bug: every wall (and each sign-in
  // prompt flavor) leaves a trace, so a member looping on an invite link shows
  // up in Logfire instead of only in a support thread.
  const viewKind = view.kind;
  const authReason = view.kind === "authRequired" ? view.reason : undefined;
  useEffect(() => {
    if (viewKind === "loading" || viewKind === "accepting" || viewKind === "joined") return;
    logger.notice("invite_wall_rendered", { kind: viewKind, reason: authReason ?? null });
  }, [viewKind, authReason]);

  switch (view.kind) {
    case "loading":
      return <Spinner label="Loading your invite…" />;

    case "previewFailed":
      return (
        <Shell>
          <h1 className="text-headline font-normal">Couldn't load this invite</h1>
          <p className="text-body text-muted-foreground">
            Something went wrong on our side. Check your connection and try again.
          </p>
          <Button variant="outline" className="h-10" onClick={() => void preview.refetch()}>
            Try again
          </Button>
        </Shell>
      );

    case "notFound":
      return (
        <Shell signOut={view.signedIn}>
          <h1 className="text-headline font-normal">Invite not found</h1>
          <p className="text-body text-muted-foreground">
            This invite link is invalid. Ask whoever invited you to send a new one.
          </p>
          <GoHome signedIn={view.signedIn} />
        </Shell>
      );

    case "authRequired": {
      const heading =
        view.reason === "accepted"
          ? {
              title: "This invite has already been used",
              subtitle: `If this was you, sign in to continue to ${orgName}.`,
            }
          : view.reason === "expired"
            ? {
                title: "This invite has expired",
                subtitle: `Invite links are valid for 7 days. Ask ${inviter} to resend it, or sign in if you already have access.`,
              }
            : {
                title: `Join ${orgName}`,
                subtitle: "Sign in to accept your invite.",
              };
      const returnTo = `/invite/${token}`;
      return (
        <ErrorBoundary name="InviteAuth">
          <AuthForm
            title={heading.title}
            subtitle={heading.subtitle}
            onGoogleSignIn={() => signInWithGoogle(returnTo)}
            onMicrosoftSignIn={() => signInWithMicrosoft(returnTo)}
            onSsoSignIn={(org) => signInWithWorkOS(org, returnTo)}
          />
        </ErrorBoundary>
      );
    }

    case "accepting":
    case "joined":
      return <Spinner label={`Joining ${orgName}…`} />;

    case "alreadyUsed":
      return (
        <Shell signOut>
          <h1 className="text-headline font-normal">This invite has already been used</h1>
          <p className="text-body text-muted-foreground">
            If this wasn't you, ask {inviter} to send a new invite.
          </p>
          <GoHome signedIn />
        </Shell>
      );

    case "expiredMember":
      return (
        <Shell signOut>
          <h1 className="text-headline font-normal">This invite has expired</h1>
          <p className="text-body text-muted-foreground">
            Invite links are valid for 7 days. Ask {inviter} to resend it.
          </p>
          <GoHome signedIn />
        </Shell>
      );

    case "rejected":
      return (
        <Shell signOut>
          <h1 className="text-headline font-normal">Can't join {orgName}</h1>
          <p className="text-body text-muted-foreground">{accept.error?.message}</p>
          <GoHome signedIn />
        </Shell>
      );

    case "acceptFailed":
      return (
        <Shell signOut>
          <h1 className="text-headline font-normal">Something went wrong</h1>
          <p className="text-body text-muted-foreground">
            We couldn't process your invite. Try again in a moment.
          </p>
          <Button
            variant="outline"
            className="h-10"
            onClick={() => {
              fired.current = false;
              accept.reset();
            }}
          >
            Try again
          </Button>
        </Shell>
      );
  }
}

export const Route = createFileRoute("/invite/$token")({
  // A different token must remount the page: the one-shot `fired` ref and the
  // accept mutation state are per-invite, and a stale `fired=true` from a
  // previous token would silently skip the auto-accept for the new one.
  remountDeps: ({ params }) => params.token,
  component: InvitePage,
});
